import { memo, useMemo, useState, useCallback, isValidElement, type ComponentType, type HTMLAttributes } from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { CopyButton } from '@/components/ui/CopyButton'
import { useAgentTheme } from '@/theme/AgentThemeProvider'

interface CodeBlockProps {
  lang?: string
  codeChildren: React.ReactNode
  allowExecution?: boolean
}

const MAX_HIGHLIGHT_CHARS = 10_000
const MAX_HIGHLIGHT_LINE_LENGTH = 1_000
const COLLAPSE_LINE_THRESHOLD = 50
const COLLAPSE_CHAR_THRESHOLD = 3_000

const extractText = (node: any): string => {
  if (node == null || node === false) return ''

  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }

  if (Array.isArray(node)) {
    return node.map(extractText).join('')
  }

  if (isValidElement(node)) {
    return extractText((node as any).props?.children)
  }

  return ''
}

const CodeBlockPreTag: ComponentType<HTMLAttributes<HTMLDivElement>> = (props) => <div {...props} />

function shouldHighlight(code: string): boolean {
  if (code.length > MAX_HIGHLIGHT_CHARS) return false
  const maxLineLen =
    code.indexOf('\n') === -1
      ? code.length
      : Math.max(
          ...code
            .split('\n')
            .slice(0, 100)
            .map((l) => l.length)
        )
  return maxLineLen <= MAX_HIGHLIGHT_LINE_LENGTH
}

function truncateCode(code: string): { truncated: string; isTruncated: boolean; totalLines: number } {
  const lines = code.split('\n')
  const totalLines = lines.length

  if (totalLines > COLLAPSE_LINE_THRESHOLD) {
    return {
      truncated: lines.slice(0, COLLAPSE_LINE_THRESHOLD).join('\n'),
      isTruncated: true,
      totalLines
    }
  }

  if (code.length > COLLAPSE_CHAR_THRESHOLD) {
    return {
      truncated: code.slice(0, COLLAPSE_CHAR_THRESHOLD),
      isTruncated: true,
      totalLines
    }
  }

  return { truncated: code, isTruncated: false, totalLines }
}

const highlighterCustomStyle: React.CSSProperties = {
  margin: 0,
  padding: '12px 16px',
  background: 'var(--mp-agents-surface)',
  fontSize: '13px',
  lineHeight: '1.5',
  overflowX: 'auto'
}

const CodeContent = memo(
  ({
    code,
    lang,
    highlight,
    prismStyle
  }: {
    code: string
    lang: string
    highlight: boolean
    prismStyle: Record<string, React.CSSProperties>
  }) => {
    if (highlight) {
      return (
        <SyntaxHighlighter
          language={lang}
          style={prismStyle}
          PreTag={CodeBlockPreTag}
          customStyle={highlighterCustomStyle}
        >
          {code}
        </SyntaxHighlighter>
      )
    }

    return (
      <pre className='mp-agent-code-block-pre'>
        <code>{code}</code>
      </pre>
    )
  }
)

CodeContent.displayName = 'CodeContent'

export const CodeBlock = memo(({ lang = 'text', codeChildren, allowExecution = false }: CodeBlockProps) => {
  const { colorMode } = useAgentTheme()
  const codeString = extractText(codeChildren).replace(/\n$/, '')
  const prismStyle = colorMode === 'light' ? oneLight : oneDark

  const { truncated, isTruncated, totalLines } = useMemo(() => truncateCode(codeString), [codeString])
  const [expanded, setExpanded] = useState(false)
  const toggle = useCallback(() => setExpanded((prev) => !prev), [])

  const visibleCode = expanded ? codeString : truncated
  const canHighlight = useMemo(() => shouldHighlight(visibleCode), [visibleCode])

  return (
    <div className='mp-agent-code-block-wrapper'>
      <div className='mp-agent-code-block-header'>
        <span className='mp-agent-code-block-language'>{lang}</span>
        <CopyButton text={codeString} />
      </div>
      <div className='mp-agent-code-block-body'>
        <CodeContent code={visibleCode} lang={lang} highlight={canHighlight} prismStyle={prismStyle} />
      </div>
      {isTruncated && (
        <button type='button' className='mp-agent-code-block-toggle' onClick={toggle}>
          {expanded
            ? 'Show less'
            : `Show more (${totalLines > COLLAPSE_LINE_THRESHOLD ? `${totalLines - COLLAPSE_LINE_THRESHOLD} more lines` : `${codeString.length - COLLAPSE_CHAR_THRESHOLD} more chars`})`}
        </button>
      )}
    </div>
  )
})

CodeBlock.displayName = 'CodeBlock'
